/**
 * ProteinContextProvider Component
 * This React component creates and provides the protein context for various forms.
 * This component must wrap the form containing ProteinSelect, a parent of ProteinSelect, or the
 * ProteinSelect component itself.
 *
 * In conjunction with ProteinSelect, the selected protein can be changed. If the intertwined form
 * is also subscribed to ProteinContext, it will be updated on any change.
*/

import React, { useState } from 'react';
import { App } from '../../BMapsApp';
import useEventBrokerSubscription from './helpers/useEventBrokerSubscription';

export const ProteinContext = React.createContext({
    selectedProtein: null,
    selectedCaseData: null,
    loadedProteins: [],
    handleContextChange: () => {},
});

export default function ProteinContextProvider({
    initialProtein: initialProteinIn, defaultToFirstProtein, children,
}) {
    const loadedProteins = App.Workspace.getLoadedProteins();
    let selectedProtein = null;
    let selectedCaseData = null;
    let initialProtein;
    if (initialProteinIn) {
        initialProtein = loadedProteins.find((p) => p === initialProteinIn);
        if (!initialProtein) {
            console.error(`ProteinContextProvider: attempted to initialize with unknown protein ${initialProteinIn}`);
        }
    } else if (defaultToFirstProtein) {
        initialProtein = loadedProteins[0];
    }
    if (loadedProteins.length === 0) {
        selectedCaseData = App.Workspace.firstCaseData(); // No protein case
    } else if (initialProtein || loadedProteins.length === 1) {
        selectedProtein = initialProtein || loadedProteins[0];
        selectedCaseData = App.Workspace.lookupCaseData(selectedProtein);
    }

    const [contextValue, setContextValue] = useState({
        selectedProtein,
        selectedCaseData,
        loadedProteins,
        handleContextChange,
        lookupCaseData,
    });

    /** @param {MapCase} protein */
    function lookupCaseData(protein) {
        return App.Workspace.lookupCaseData(protein);
    }

    function handleContextChange(p) {
        const caseData = App.Workspace.lookupCaseData(p);
        setContextValue((c) => ({
            ...c,
            selectedProtein: p,
            selectedCaseData: caseData,
        }));
    }

    const handleLoadedProteins = () => {
        const newLoadedProteins = App.Workspace.getLoadedProteins();
        setContextValue((c) => {
            // Keep the current selection if it is still loaded.
            if (c.selectedProtein && newLoadedProteins.includes(c.selectedProtein)) {
                return { ...c, loadedProteins: newLoadedProteins };
            }
            // The selected protein was unloaded (or none was selected): recompute the way the
            // initial mount does, so the selection never dangles on an unloaded protein (which
            // would leave the picker showing a value with no matching option).
            let nextProtein = null;
            let nextCaseData = null;
            if (newLoadedProteins.length === 0) {
                nextCaseData = App.Workspace.firstCaseData(); // No protein case
            } else if (defaultToFirstProtein || newLoadedProteins.length === 1) {
                nextProtein = newLoadedProteins[0];
                nextCaseData = App.Workspace.lookupCaseData(nextProtein);
            }
            return {
                ...c,
                loadedProteins: newLoadedProteins,
                selectedProtein: nextProtein,
                selectedCaseData: nextCaseData,
            };
        });
    };
    useEventBrokerSubscription('proteinsChanged', handleLoadedProteins);

    return (
        <ProteinContext.Provider value={contextValue}>
            {children}
        </ProteinContext.Provider>
    );
}
